import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { getCardCategory, CARD_CATEGORIES } from "@/lib/cardCategories";

const QUESTIONS = [
  {
    q: "Where does most of your spending go?",
    options: [
      { label: "Everyday essentials & bills", desc: "Groceries, utilities, online shopping", scores: { cashback: 2, rewards: 1 } },
      { label: "Flights, hotels & travel", desc: "Work trips or holidays", scores: { travel: 3 } },
      { label: "Fuel & commuting", desc: "Daily driving, multiple vehicles", scores: { fuel: 3 } },
      { label: "Shopping, dining & lifestyle", desc: "The fun stuff", scores: { rewards: 3 } },
    ],
  },
  {
    q: "Do you pay your card bill in full every month?",
    options: [
      { label: "Yes, always", desc: "I clear the full amount", scores: {} },
      { label: "Sometimes I carry a balance", desc: "I pay part or the minimum", scores: {}, caution: true },
      { label: "I'm new to credit cards", desc: "This would be my first card", scores: { cashback: 1 } },
    ],
  },
  {
    q: "How often do you fly in a year?",
    options: [
      { label: "Rarely", desc: "0–2 flights", scores: {} },
      { label: "A few times", desc: "3–8 flights", scores: { travel: 2 } },
      { label: "Constantly", desc: "9+ flights", scores: { travel: 3 } },
    ],
  },
];

export default function CardFinderPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === QUESTIONS.length;

  useSEO({
    title: "Card Finder — Which Credit Card Fits You? 3 Questions | Loan Salaah",
    description: "Answer 3 quick questions and we'll point you to the credit card category that fits your spending — rewards, travel, cashback or fuel.",
  });

  const result = useMemo(() => {
    if (!done) return null;
    const totals = { rewards: 0, travel: 0, cashback: 0, fuel: 0 };
    let caution = false;
    answers.forEach((a) => {
      Object.entries(a.scores).forEach(([k, v]) => { totals[k] += v; });
      if (a.caution) caution = true;
    });
    const ranked = Object.entries(totals).sort((x, y) => y[1] - x[1]);
    return { winner: ranked[0][0], runnerUp: ranked[1][0], caution };
  }, [answers, done]);

  const answer = (opt) => {
    setAnswers((prev) => [...prev, opt]);
    setStep((s) => s + 1);
  };

  const back = () => {
    setAnswers((prev) => prev.slice(0, -1));
    setStep((s) => Math.max(0, s - 1));
  };

  const retake = () => {
    setAnswers([]);
    setStep(0);
  };

  const winner = result ? getCardCategory(result.winner) : null;
  const runnerUp = result ? getCardCategory(result.runnerUp) : null;

  return (
    <div data-testid="card-finder-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <Reveal className="text-center">
          <div className="flex items-center justify-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">30-second card finder</span>
            <span className="brass-rule" aria-hidden="true" />
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl">
            Which card fits the way you spend?
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate">
            Three questions. No email, no sign-up, no credit check — just a pointed recommendation.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-3xl border border-sand bg-white p-6 shadow-[0_30px_80px_rgba(20,40,75,0.1)] sm:p-10">
            {!done ? (
              <>
                <div className="mb-8 flex items-center gap-2" data-testid="quiz-progress">
                  {QUESTIONS.map((_, i) => (
                    <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${i < step ? "bg-brass" : i === step ? "bg-concordia" : "bg-sand"}`} />
                  ))}
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <p className="font-mono text-[0.62rem] uppercase tracking-[0.28em] text-slate">Question {step + 1} of {QUESTIONS.length}</p>
                    <h2 className="mt-2 font-serif text-2xl font-semibold text-concordia sm:text-3xl" data-testid="quiz-question">
                      {QUESTIONS[step].q}
                    </h2>
                    <div className="mt-6 space-y-3">
                      {QUESTIONS[step].options.map((opt, i) => (
                        <button
                          key={opt.label}
                          data-testid={`quiz-option-${i}`}
                          onClick={() => answer(opt)}
                          className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-sand bg-cream p-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-concordia/40 hover:bg-white hover:shadow-[0_14px_30px_rgba(20,40,75,0.1)]"
                        >
                          <span>
                            <span className="block text-sm font-semibold text-charcoal">{opt.label}</span>
                            <span className="mt-0.5 block text-xs text-slate">{opt.desc}</span>
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-brass transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                      ))}
                    </div>
                    {step > 0 && (
                      <button data-testid="quiz-back-button" onClick={back}
                        className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-slate transition-colors hover:text-concordia">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="quiz-result">
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.28em] text-slate">Your match</p>
                <div className="mt-4 rounded-2xl bg-concordia-ink p-7 text-ivory">
                  <Sparkles className="h-6 w-6 text-brass" />
                  <h2 className="mt-3 font-serif text-3xl font-semibold" data-testid="quiz-result-category">{winner.name}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/70">{winner.short}</p>
                </div>
                {result.caution && (
                  <p className="mt-4 rounded-xl border border-brass/40 bg-champagne/30 px-4 py-3 text-xs leading-relaxed text-charcoal/85" data-testid="quiz-caution-note">
                    Since you sometimes carry a balance: interest (around 3.5–3.9% a month) wipes out any reward.
                    Pick a low-fee card and clear the full bill — that's worth more than any points programme.
                  </p>
                )}
                {runnerUp && (
                  <p className="mt-4 text-sm text-slate">
                    Also worth a look:{" "}
                    <Link to={`/loans/credit-cards/${runnerUp.slug}`} data-testid="quiz-runnerup-link" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-4 hover:text-brass-deep">
                      {runnerUp.name}
                    </Link>
                  </p>
                )}
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link to={`/loans/credit-cards/${winner.slug}`} data-testid="quiz-explore-button"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-concordia/25 px-6 py-3.5 text-sm font-semibold text-concordia transition-colors hover:border-brass hover:text-brass-deep">
                    Explore {winner.name.replace(" Credit Cards", "")} cards
                  </Link>
                  <Link to="/apply?product=credit-cards" data-testid="quiz-apply-button"
                    className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-concordia px-6 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(20,40,75,0.35)]">
                    Get my card matched
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
                <button data-testid="quiz-retake-button" onClick={retake}
                  className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-slate transition-colors hover:text-concordia">
                  <RotateCcw className="h-3.5 w-3.5" /> Retake the quiz
                </button>
              </motion.div>
            )}
          </div>
          <p className="mt-5 text-center text-xs leading-relaxed text-slate/80">
            This is guidance, not an approval. Loan Salaah facilitates cards from partner banks; the issuing bank
            makes every decision, and we never pull or calculate your credit score.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
